<script>
   /****************************************************************/
   /* PlotPLSVarsel                                                */
   /* --------------------                                         */
   /* shows plot for variable selection in PLS loadings            */
   /****************************************************************/

   import { Area } from 'svelte-plots-basic/2d';
   import { vector, Matrix } from 'mdatools/arrays';
   import { mrange } from 'mdatools/stat';
   import { LOADINGS_LINE_COLOR, getLimX } from './Defaults';

   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';
   import { csvRow, downloadCSV } from '../utils.js';


   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // selection states
   let dontClick = $state(true);
   let slcStarted = $state(false);
   let sx = $state(null);
   let sy = $state(null);
   let ex = $state(null);
   let ey = $state(null);

   // get model object from properties
   const model = $derived(props ? props.model : null);
   const ncomp = $derived(props && props.ncomp > 0? props.ncomp : 1);


   // get array with selectivity ratios and vip scores and convert it to matrix
   const start = $derived(model && ncomp > 0 ? (ncomp - 1) * model.npred * model.nresp : null);
   const end = $derived(start !== null ? start + model.npred * model.nresp : null);
   const sr = $derived(end !== null ? new Matrix(model.sr.subarray(start, end), model.npred, model.nresp) : null);
   const vip = $derived(end !== null ? new Matrix(model.vip.subarray(start, end), model.npred, model.nresp) : null);

   // the selection is drawn as a polygon through its corners, not as a rectangle: on a reversed
   // axis the library scales a width by a negative factor and the SVG rectangle disappears
   const selX = $derived(sx !== null && ex !== null ? [sx, ex, ex, sx] : null);
   const selY = $derived(sy !== null && ey !== null ? [sy, sy, ey, ey] : null);


   // get values and indices for x-variables
   const xvalues = $derived(model ? vector(model.xvarvalues) : null);
   const xindices = $derived(model ? vector(model.xvarindices) : null);
   const labels = $derived(model ? model.xvarlabels : null);

   const xValuesSet = $derived(xvalues && xindices && vip ? {
      'values': xvalues,
      'indices': xindices,
      'vip': vip.getcolumn(params.resp),
   } : null);

   const yValuesSet = $derived(vip && sr ? {
      'vip': vip.getcolumn(params.resp),
      'selratio': sr.getcolumn(params.resp),
   } : null);

   const xLabelsSet = $derived(model ? {
      'values': model.varvaluesName + (model.varvaluesUnits ? ', ' + model.varvaluesUnits : ''),
      'indices': 'Predictors',
      'vip': 'VIP scores',
   } : null);

   const yLabelsSet = $derived(model ? {
      'vip': 'VIP scores',
      'selratio': 'Selectivity ratio',
   } : null);

   const xValues = $derived(xValuesSet ? xValuesSet[params.xset] : null);
   const yValues = $derived(yValuesSet ? yValuesSet[params.yset] : null);

   const xLabel = $derived(xLabelsSet ? xLabelsSet[params.xset] : null);
   const yLabel = $derived(yLabelsSet ? yLabelsSet[params.yset] : null);
   const title = $derived('Variable importance ' + model.varlabels[params.resp - 1] + ' (A = ' + ncomp + ')');

   // the axis follows the model's direction only when it shows the variable values
   const limX = $derived(getLimX(xValues, 'l', model.varrev && params.xset === 'values'));
   const limY = $derived(yValues ? mrange(yValues, 0.075) : [0, 1]);
   const markerSize = $derived(model ? (model.npred < 30 ? 1 : model.npred > 200 ? 0.5 : 0.8) : 1);

   // name of the image and CSV files
   const fileName = 'plot-varimp';

   const xValuesSel = $derived(selected && selected.length > 0 ?
      vector(selected.map(i => xValues.v[i]))
   : null);

   const yValuesSel = $derived(selected && selected.length > 0 ?
      vector(selected.map(i => yValues.v[i]))
   : null);

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      const { sep, dec } = Object.values(props.data)[0];
      const resp = model.nresp > 1 ? ' (' + model.varlabels[params.resp - 1] + ')' : '';
      let out = csvRow(['Labels', xLabel, yLabel + resp], sep, dec);
      for (let i = 0; i < labels.length; i++) {
         out += csvRow([labels[i], xValues.v[i], yValues.v[i]], sep, dec);
      }
      downloadCSV(out, fileName);
   }

   // methods
   function select() {
      // bounds from the drag coordinates in either order, so the axis direction plays no part
      const left = Math.min(sx, ex);
      const right = Math.max(sx, ex);
      const bottom = Math.min(sy, ey);
      const top = Math.max(sy, ey);

      const selind = [];
      const selx = [];
      const sely = [];

      for (let i = 0; i < xValues.length; i++) {
         const xv = xValues.v[i];
         const yv = yValues.v[i];
         if (xv >= left && xv <= right && yv >= bottom && yv <= top) {
            selind.push(i)
            selx.push(xv)
            sely.push(yv)
         }
      }

      if (selind.length > 0) {
         callbacks.select(selind);
      }

      sx = null;
      sy = null;
      ex = null;
      ey = null;
   }

   function startSelection(x, y) {
      callbacks.unselect();
      slcStarted = true;
      sx = x;
      sy = y;
      ex = x;
      ey = y;
   }

   function stopSelection(x, y) {
      slcStarted = false;
      dontClick = true;
      select();
   }

   function moveSelection(x, y) {
      if (!slcStarted) return;
      ex = x;
      ey = y;
   }

   function cancelSelection() {
      if (dontClick) {
         dontClick = false;
         return;
      }

      slcStarted = false;
      sx = null;
      sy = null;
      ex = null;
      ey = null;
      callbacks.unselect();
   }

</script>

{#if xValues && yValues }
<div>
<Axes {xLabel} {yLabel} {limX} {limY} {title}
   onmousedown={startSelection}
   onmouseup={stopSelection}
   onmousemove={moveSelection}
   onclick={cancelSelection}
   {fileName} {downloadCSVCallback}>

   <!-- data points  -->
   <PlotSeries
      {xValues}
      {yValues}
      {markerSize}
      mainColor={LOADINGS_LINE_COLOR + (selected && selected.length > 0 ? '80' : '')}
      plotType='p'
      showLabels={params.showLabels}
      labels={labels}
      shortenLabels={false}
   />

   {#if selX && selY && slcStarted}
   <Area xValues={selX} yValues={selY} faceColor="#ff000040" lineColor="#ff000040" />
   {/if}

   {#if xValuesSel && yValuesSel }
   <PlotSeries
      xValues={xValuesSel}
      yValues={yValuesSel}
      {markerSize}
      mainColor="red"
      plotType='p'
      shortenLabels={false}
   />
   {/if}
</Axes>
</div>
{/if}
<style>
   div {
      width: 100%;
      height: 100%;
   }

   div  :global(.plot .axes) {
      cursor: crosshair;
   }

   div :global(.plot .axes-content polygon) {
      pointer-events: none;
   }
</style>